import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { AtsPassButton } from "@/components/AtsPassButton";
import { useResumeUpload } from "@/components/InlineResumeUpload";
import { ResumeVersions } from "@/components/profile/ResumeVersions";
import { AtsAutomation } from "@/components/profile/AtsAutomation";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronRight, ExternalLink, FilePen, FileUp, Heart, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, PageHeader, Spinner, TierBadge } from "@/components/Common";
import { openTour } from "@/lib/onboarding";
import { PlayCircle } from "lucide-react";
import { Sections } from "@/components/Rich";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, openApiPath } from "@/lib/api";
import { GoalsSection, LinksSection, SkillsSection, SnapshotSection } from "@/components/profile/ProfileSections";
import { Documents } from "@/components/profile/Documents";
import { ProfileStrength } from "@/components/profile/ProfileStrength";
import { PublicProfileSection } from "@/components/profile/PublicProfileSection";
import { EXTENDED_DEFAULTS } from "@/components/profile/options";
import { BillingCard } from "@/components/profile/BillingCard";
import { DangerZone } from "@/components/profile/DangerZone";
import { useBillingReturn } from "@/lib/billing";

const ResumeTips = () => {
  const [tips, setTips] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/ai/resume-tips").then((r) => r.data && setTips(r.data)).catch(() => {}); }, []);
  const gen = async () => { setBusy(true); try { setTips((await api.post("/ai/resume-tips")).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const has = tips && (tips.sections || tips.tips || Object.keys(tips).length > 0);
  return (
    <Card>
      <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-lg text-zinc-100">AI resume review</h3>
        <Button size="sm" variant="outline" onClick={gen} disabled={busy} data-testid="resume-tips-button">{busy ? <Spinner /> : <Sparkles className="h-4 w-4 text-[#D4AF37]" />}{has ? "Refresh" : "Review my resume"}</Button></div>
      {has ? <div data-testid="resume-tips-result"><Sections result={tips} /></div> : <p className="text-sm text-zinc-500">Get specific, line-level tips to sharpen your resume.</p>}
    </Card>
  );
};


const KEYS = ["headline", "location", "skills", "resume_text", ...Object.keys(EXTENDED_DEFAULTS)];
const fromProfile = (p) => Object.fromEntries(KEYS.map((k) => [k, p?.[k] ?? (k in EXTENDED_DEFAULTS ? EXTENDED_DEFAULTS[k] : k === "skills" ? [] : "")]));
const fixUrl = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);
const NAV = [["snapshot", "Snapshot"], ["goals", "Goals"], ["skills", "Skills"], ["links", "Links"], ["documents", "Documents"], ["public", "Public card"], ["billing", "Billing"], ["danger-zone", "Delete data"]];

const ResumeFileCard = ({ f, set, highlight, returnJob }) => {
  const { profile, refreshProfile } = useAuth();
  const [busy, setBusy] = useState("");
  const [replaced, setReplaced] = useState(false);
  const [job, setJob] = useState(null);
  const ref = useRef(null);
  useEffect(() => { if (returnJob) api.get(`/jobs/${encodeURIComponent(returnJob)}`).then((r) => setJob(r.data?.job || null)).catch(() => {}); }, [returnJob]);
  const { upload: doUpload, busy: uploading } = useResumeUpload((p) => { if (p?.resume_text) set("resume_text", p.resume_text); setReplaced(true); }, returnJob);
  const upload = (file) => doUpload(file);
  const autofill = async () => {
    setBusy("autofill");
    try { const { data } = await api.post("/profile/autofill"); if (data.headline) set("headline", data.headline); if (data.location) set("location", data.location); if (data.skills?.length) set("skills", data.skills); toast.success("Prefilled. Review and save."); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <Card id="main-resume" className={cn("scroll-mt-24 transition-[box-shadow,border-color] duration-700", highlight && "border-[#D4AF37] shadow-[0_0_40px_-10px_rgba(212,175,55,0.6)]")} data-testid="main-resume-card">
      <h3 className="font-display text-lg text-zinc-100">Main resume</h3>
      {highlight && !replaced && <p className="mt-2 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-2.5 text-xs text-[#F3C649]" data-testid="replace-resume-hint">Upload your optimized resume below. It replaces your main resume everywhere: matching, cover letters, auto-apply and the mobile app.</p>}
      {replaced && returnJob && (
        <div className="mt-3 flex flex-col gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3" data-testid="resume-replaced-recheck">
          <span className="text-xs text-emerald-300">New resume is live. See how much your score improved{job?.title ? ` for ${job.title}` : ""}.</span>
          <AtsPassButton job={{ id: returnJob, title: job?.title || "This job", company: job?.company || "" }} />
        </div>
      )}
      <p className="mt-1 truncate text-sm text-zinc-500" data-testid="profile-resume-file">{profile.resume_file?.name || "No PDF on file"}</p>
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="profile-resume-input" />
      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={!!busy || uploading} data-testid="profile-upload-button">{uploading ? <Spinner /> : <FileUp className="h-4 w-4" />}Replace PDF</Button>
        {profile.resume_file && <Button size="sm" variant="ghost" onClick={() => openApiPath(() => api.post("/profile/resume/link")).catch((e) => toast.error(errMsg(e)))} data-testid="view-resume-button"><ExternalLink className="h-4 w-4" />View</Button>}
        <Link to="/resume-studio"><Button size="sm" variant="outline" className="border-[#D4AF37]/50 text-[#F3C649]" data-testid="profile-edit-resume-button"><FilePen className="h-4 w-4" />Edit resume</Button></Link>
        <Link to="/resume-studio?view=library"><Button size="sm" variant="ghost" data-testid="profile-tailored-library-link">Tailored versions</Button></Link>
        <Button size="sm" variant="ghost" onClick={autofill} disabled={!!busy} data-testid="profile-autofill-button">{busy === "autofill" ? <Spinner /> : <Sparkles className="h-4 w-4 text-[#D4AF37]" />}Autofill</Button>
      </div>
      <details className="mt-4">
        <summary className="cursor-pointer text-xs text-zinc-500">Edit resume text</summary>
        <Textarea rows={8} value={f.resume_text} onChange={(e) => set("resume_text", e.target.value)} className="mt-3 bg-[#09090B] text-xs" data-testid="profile-resume-text" />
      </details>
    </Card>
  );
};

export default function Profile() {
  const { profile, refreshProfile, user, tier, logout } = useAuth();
  const [params] = useSearchParams();
  const focusResume = params.get("focus") === "resume";
  useEffect(() => { if (focusResume) setTimeout(() => document.getElementById("main-resume")?.scrollIntoView({ behavior: "smooth", block: "center" }), 300); }, [focusResume]);
  useBillingReturn();
  const [saved, setSaved] = useState(() => fromProfile(profile));
  const [f, setF] = useState(saved);
  const [busy, setBusy] = useState(false);
  const legacy = !("bio" in (profile || {}));
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const dirty = JSON.stringify(f) !== JSON.stringify(saved);

  const save = async () => {
    if (!f.headline.trim() || !f.skills.length) return toast.error("Headline and at least one hard skill are required");
    setBusy(true);
    const body = { ...f, linkedin_url: fixUrl(f.linkedin_url), github_url: fixUrl(f.github_url), portfolio_url: fixUrl(f.portfolio_url) };
    try {
      await api.put("/profile", body);
      const p = await refreshProfile();
      const ok = !!p && "bio" in p;
      const next = ok ? fromProfile(p) : body;
      setSaved(next); setF(next);
      if (ok) toast.success("Profile saved");
      else toast.warning("Headline, location and skills saved. The new fields will save once the backend update is live.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="pb-24">
      <PageHeader title="Profile" subtitle="The richer your profile, the sharper every match, letter and agent run.">
        <Button variant="outline" onClick={() => openTour("spotlight")} data-testid="profile-replay-tour"><PlayCircle className="h-4 w-4" />Replay app tour</Button>
        <Button variant="outline" onClick={() => openTour("product")} data-testid="profile-product-tour"><Sparkles className="h-4 w-4" />Product tour</Button>
        <Button variant="ghost" onClick={logout} data-testid="profile-logout-button">Log out</Button>
      </PageHeader>
      {legacy && (
        <div className="mb-6 flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="backend-pending-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />The extended profile fields and the document library need a backend update in the RPOPRO mobile project. Until then, only headline, location, skills and the main resume are saved.
        </div>
      )}
      <div className="sticky top-16 z-10 -mx-4 mb-6 flex items-center gap-4 border-b border-[#27272A] bg-[#09090B]/90 px-4 py-3 backdrop-blur-md md:mx-0 md:rounded-xl md:border">
        <div className="flex flex-1 gap-1 overflow-x-auto">
          {NAV.map(([id, l]) => <a key={id} href={`#${id}`} onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); }} className="shrink-0 rounded-full px-3 py-1 text-xs text-zinc-400 transition-colors hover:bg-[#18181B] hover:text-[#F3C649]" data-testid={`profile-nav-${id}`}>{l}</a>)}
        </div>
        <div className="hidden" data-testid="profile-completion" />
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-6">
          <SnapshotSection f={f} set={set} />
          <GoalsSection f={f} set={set} />
          <SkillsSection f={f} set={set} />
          <LinksSection f={f} set={set} />
          <ResumeTips />
        </div>
        <div className="space-y-6">
          <Card className="flex items-center gap-4">
            {user?.picture && <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-[#D4AF37]/40" />}
            <div className="min-w-0"><div className="truncate font-display text-xl text-zinc-100" data-testid="profile-name">{user?.name}</div><div className="truncate text-sm text-zinc-500">{user?.email}</div><TierBadge tier={tier} className="mt-1" /></div>
          </Card>
          <ProfileStrength f={f} />
          <ResumeFileCard f={f} set={set} highlight={focusResume} returnJob={params.get("job")} />
          <ResumeVersions onRestored={(p) => p?.resume_text && set("resume_text", p.resume_text)} />
          <AtsAutomation />
          <Documents />
          <PublicProfileSection f={f} dirty={dirty} />
          <BillingCard />
          <Card className="space-y-1 p-2" data-testid="profile-wellbeing-card">
            {[
              ["/wellbeing", "Well-Being", "check-ins, wisdom, journal & your companion", Heart, "profile-wellbeing-row"],
              ["/wellbeing/settings", "Well-Being privacy & reminders", "choose what the AI may use, emails, delete journal", ShieldCheck, "profile-wellbeing-settings-row"],
            ].map(([to, t, d, Icon, id]) => (
              <Link key={to} to={to} data-testid={id} className="flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-[#33290D]/60">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#33290D] text-[#D4AF37]"><Icon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-medium text-zinc-100">{t}</span><span className="block text-xs text-zinc-500">{d}</span></span>
                <ChevronRight className="h-4 w-4 text-zinc-600" />
              </Link>
            ))}
          </Card>
          <DangerZone />
        </div>
      </div>
      {dirty && createPortal(
        <div className="fixed inset-x-0 bottom-[84px] z-30 border-t border-[#D4AF37]/30 lg:bottom-0 bg-[#09090B]/95 backdrop-blur-md lg:left-64 fade-up" data-testid="profile-save-bar">
          <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3 px-4 py-3 md:px-8">
            <span className="text-sm text-zinc-400">Unsaved changes</span>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setF(saved)} data-testid="profile-discard-button">Discard</Button>
              <Button onClick={save} disabled={busy} data-testid="profile-save-button">{busy ? <Spinner /> : "Save profile"}</Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
